import { Add as AddIcon, Save as SaveIcon } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";
import { unwrapResult } from "@reduxjs/toolkit";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import { SyncStockPart } from "components/stock/SyncStockPart";
import { StyledContainer } from "components/StyledContainer";
import StyledFab from "components/StyledFab";
import { StockSync } from "models/StockSync";
import { TabProps } from "models/TabProps";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { getTechnicians } from "store/slices/cache.store";
import {
  getTechStock,
  moveMobileStock,
  removeStockToSync,
  updateMobileStock,
  updateStockToSync,
} from "store/slices/stock.store";
import { SelectStockToSyncDialog } from "./SelectStockToSyncDialog";

interface StockSyncProps extends TabProps {
  moveStock: boolean;
}

export const StockSyncTab: FC<StockSyncProps> = ({ setFab, moveStock }) => {
  const dispatch = useAppDispatch();
  const { loadingStockEnquiry, stockToMove, stockToUpdate } = useAppSelector((s) => s.stock);
  const [openSelectDialog, setOpenSelectDialog] = useState<boolean>(false);

  const onSyncPart = useCallback(
    (request: StockSync) => {
      dispatch(
        updateStockToSync({
          stock: request,
          moveStock,
        }),
      );
    },
    [dispatch, moveStock],
  );

  useEffect(() => {
    if (moveStock) dispatch(getTechnicians());
  }, [dispatch, moveStock]);

  const onRemovePart = useCallback(
    (request: StockSync) => {
      dispatch(
        removeStockToSync({
          stock: request,
          moveStock,
        }),
      );
    },
    [dispatch, moveStock],
  );

  const partListItems = useMemo(() => {
    const stocks = moveStock ? stockToMove : stockToUpdate;
    return stocks.map((stock) => {
      return (
        stock && (
          <SyncStockPart
            stock={stock}
            key={`${stock.sync.articleNo}`}
            onSyncPart={onSyncPart}
            onRemovePart={onRemovePart}
            moveStock={moveStock}
          />
        )
      );
    });
  }, [moveStock, stockToMove, stockToUpdate, onSyncPart, onRemovePart]);

  const onSave = useCallback(() => {
    dispatch(
      moveStock
        ? moveMobileStock({
            stocks: stockToMove.map((s) => s.sync),
          })
        : updateMobileStock({ stocks: stockToUpdate.map((s) => s.sync) }),
    )
      .then(unwrapResult)
      .then((_) => {
        dispatch(getTechStock());
      });
  }, [dispatch, moveStock, stockToMove, stockToUpdate]);

  useEffect(() => {
    const fab = (
      <>
        <Grid item>
          <StyledFab onClick={() => setOpenSelectDialog(true)}>
            <AddIcon />
          </StyledFab>
        </Grid>
        <Grid item>
          <StyledFab
            onClick={() => onSave()}
            disabled={moveStock ? stockToMove.length === 0 : stockToUpdate.length === 0}
          >
            <SaveIcon />
          </StyledFab>
        </Grid>
      </>
    );
    if (setFab) {
      setFab(fab);
    }
  }, [dispatch, moveStock, onSave, setFab, stockToMove.length, stockToUpdate.length]);

  return (
    <StyledContainer data-testid="StockSyncTab">
      <BackdropPrimaryMain open={loadingStockEnquiry}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <Grid container item spacing={1}>
        {partListItems && partListItems?.length > 0 ? partListItems : null}
      </Grid>
      <SelectStockToSyncDialog
        open={openSelectDialog}
        setOpenAddPartDialog={setOpenSelectDialog}
        handleClose={() => setOpenSelectDialog(false)}
        loading={loadingStockEnquiry}
        moveStock={moveStock}
      />
    </StyledContainer>
  );
};
